/**
 * Admin metrics & logs API client for `app/api/v1/admin_metrics.py` (plan section 17.2).
 *
 * Written against that module's actual, already-landed response schemas
 * (`app/schemas/metrics.py`) rather than the plan prose alone -- the backend agent's build
 * shipped ahead of this one and its `OverviewResponse`/`ByModelRow`/`ReviewOutcomesResponse`/
 * `SystemStatusResponse` shapes are the ground truth this file mirrors field-for-field
 * (e.g. `ReviewOutcomesResponse.outcomes`, not `.rows`; `ByModelRow` has no
 * `cache_read_tokens`; `/time-series` points carry no `period_label`; a tenth endpoint,
 * `/videos-list`, exists specifically to populate 17.3's Video filter).
 *
 * Every GET here is a thin passthrough returning the *full axios response*, matching this
 * repo's own house style (videosApi.js, findingsApi.js -- callers unwrap `.data` themselves
 * via react-query's `select`), not the main app's `adminUsageAnalyticsService.js`, which
 * unwraps to `.data` inside the service. A new module rather than an addition to
 * `services/api.js`: every other phase's routes already get their own file here.
 *
 * The two CSV exports are the one exception to "just build a URL": every route here (see
 * `admin_metrics.py`'s `dependencies=[Depends(require_admin)]`, backed by `HTTPBearer`) needs
 * an `Authorization: Bearer <token>` header, which only `api`'s axios interceptor attaches --
 * a plain `<a href>` navigation carries no such header and would 401. So `getEventsCsv`/
 * `getRunsCsv` fetch the CSV as a `blob` through axios (interceptor included) and the page
 * turns that blob into a real file download via an object URL, instead of rendering a link
 * that looks clickable but 401s the moment someone actually clicks it.
 */
import api from './api';

const BASE = '/api/v1/admin/metrics';

// Drop null/undefined/'' params so a cleared filter is simply absent from the query string
// instead of sending e.g. `project_id=` for the backend to special-case. Mirrors the main
// app's adminUsageAnalyticsService's own clean-before-GET step.
function clean(params = {}) {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== '')
  );
}

export const metricsAPI = {
  /**
   * GET /overview -- params: start_date, end_date, range, project_id, video_id, event_type.
   * Response (`OverviewResponse`): { total_runs, unique_videos, total_tokens,
   *   total_tokens_input, total_tokens_output, total_cost_usd, total_cache_read_tokens,
   *   by_event_type: [{event_type, event_count, tokens_input, tokens_output, tokens_total,
   *   cost_usd}], by_model: [{model_id, role, calls, input_tokens, output_tokens, cost_usd,
   *   avg_latency_ms, fallback_count, source}] }. `avg_latency_ms`/`fallback_count` are
   *   `null` (never fabricated as 0) and `source` is `"pipeline_runs"` rather than
   *   `"usage_metrics"` for any run that predates the per-call hook; `total_cache_read_tokens`
   *   is honestly 0 until then too.
   */
  getOverview: (params) => api.get(`${BASE}/overview`, { params: clean(params) }),

  /**
   * GET /time-series -- params: same filters as overview, plus `granularity` (day|hour) and
   * `metric` (accepted for API parity; the server always returns every numeric field per
   * bucket, so metric selection happens client-side). Response (`TimeSeriesResponse`):
   * { start_date, end_date, granularity, series: [{period, tokens_total, tokens_input,
   *   tokens_output, cost_usd, event_count, source}] }, zero-filled across the whole range.
   * `period` is a bare ISO date/hour string with no separate display label.
   */
  getTimeSeries: (params) => api.get(`${BASE}/time-series`, { params: clean(params) }),

  /**
   * GET /runs -- params: project_id, video_id, state, page, limit.
   * Response (`RunsListResponse`): { runs: [RunSummary], total, page, limit } -- every
   *   RunSummary field is a real `pipeline_runs` column (id, video_id, project_id, user_id,
   *   state, stage, stages_completed, started_at, finished_at, error, counts, stage_timings,
   *   usage, budget_exceeded, model_ids, created_at).
   */
  getRuns: (params) => api.get(`${BASE}/runs`, { params: clean(params) }),

  /**
   * GET /runs/{run_id}. Response (`RunDetailResponse`): { run: RunSummary, calls:
   *   [{id, timestamp, event_type, model_id, role, tokens_input, tokens_output,
   *   cache_read_tokens, cost_usd, latency_ms, fallback_used, error_details}], calls_source }.
   *   `calls_source` is `"none"` (not an empty-but-real `"usage_metrics"`) when no per-call
   *   row exists for this run -- the page must render that as an honest empty state.
   */
  getRun: (runId) => api.get(`${BASE}/runs/${runId}`),

  /**
   * GET /events -- same filters as overview, plus limit, offset.
   * Response (`EventsListResponse`): { events: [EventOut], total, limit, offset }.
   */
  getEvents: (params) => api.get(`${BASE}/events`, { params: clean(params) }),

  /**
   * GET /review-outcomes -- params: start_date, end_date, project_id (no video_id -- the
   * router does not accept one for this endpoint).
   * Response (`ReviewOutcomesResponse`): { start_date, end_date, outcomes:
   *   [{video_id, video_title, project_id, run_id, approved, rejected, pending, edited,
   *   matched, new, missed, review_minutes}] }. `matched`/`new`/`missed`/`review_minutes` are
   *   `null` (unknown), never fabricated as 0, when a video has no run or fewer than two
   *   review actions.
   */
  getReviewOutcomes: (params) => api.get(`${BASE}/review-outcomes`, { params: clean(params) }),

  /**
   * GET /logs -- params: video_id, run_id, level, since, search, limit.
   * Response (`LogsResponse`): { entries: [{timestamp, level, event, video_id, run_id,
   *   stage}], source_files: [...] }. `source_files` is `[]` when the JSONL processor has
   *   never written a file yet -- an honest empty result, not an error.
   */
  getLogs: (params) => api.get(`${BASE}/logs`, { params: clean(params) }),

  /**
   * GET /system -- no params. Response (`SystemStatusResponse`): { version, environment,
   *   force_mock_ai, database: {configured, detail}, s3: {configured, detail},
   *   bedrock: {configured, detail}, s3_pending_uploads, cloudwatch_query }. Every field is a
   *   settings read, a config-only check (`s3_service.available`, never a live AWS call), or
   *   one trivial `SELECT 1`/`COUNT(*)`; `cloudwatch_query` is server-built, real, ready to copy.
   */
  getSystem: () => api.get(`${BASE}/system`),

  /**
   * GET /videos-list -- params: project_id, limit. Not one of 17.2's original nine; added
   * specifically so 17.3's Video filter has something real to populate (map section 6, item
   * 5). Response (`VideosListResponse`): { videos: [{id, title, project_id, status}] }.
   */
  getVideosList: (params) => api.get(`${BASE}/videos-list`, { params: clean(params) }),

  /**
   * GET /events.csv, fetched as a blob (see module docstring) so the caller can trigger a
   * real authenticated file download. Same filters as /events.
   */
  getEventsCsv: (params) =>
    api.get(`${BASE}/events.csv`, { params: clean(params), responseType: 'blob' }),

  /**
   * GET /runs.csv, fetched as a blob for the same reason. Same filters as /runs.
   */
  getRunsCsv: (params) =>
    api.get(`${BASE}/runs.csv`, { params: clean(params), responseType: 'blob' }),
};

export default metricsAPI;
